import { LitElement, html, css, PropertyValues, PropertyValueMap } from 'lit';
import { customElement, property } from 'lit/decorators';
import { getFormattedDateTime } from '../../../src/utils/helpers';
import { VoyageShoreContextModel } from '../../../src/models/voyage-shore-context-model';
import { ShoreContextEvent } from '../../../src/models/types/voyage-shore-context-model.types';

@customElement("voyage-time-slider-control")
export class SliderComponent extends LitElement {
    static styles = css`
    .input-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        width: 95%;
        margin: auto;
        padding: 12px;
        background: white;
        position: relative;
        bottom: 32px;
        border-radius: 4px;
        border: 1px solid #CFCFCF;
    }
    .slider {
      -webkit-appearance: none;
      width: 100%;
      height: 10px;
      background: linear-gradient(to right, #0078d7 0%, #0078d7 50%, #ccc 50%, #ccc 100%);
      border-radius: 5px;
      outline: none;
      opacity: 0.9;
      transition: opacity 0.2s;
    }
    .slider::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 20px;
      height: 20px;
      background: #0078d7;
      border-radius: 50%;
      cursor: pointer;
      box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
    }
    .slider::-moz-range-thumb {
      width: 20px;
      height: 20px;
      background: #0078d7;
      border-radius: 50%;
      cursor: pointer;
      box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
    }
    .labels {
        display: flex;
        justify-content: space-between;
        color: black;
        position: relative;
        width: 100%;
        margin: auto;
        bottom: 50px;
        flex-direction: row;
        font-family: Maersk Text;
        font-size: 12px;
        font-weight: 400;
        line-height: 20px;
    }
    .label {
      flex: 1;
      text-align: start;
      margin-left: 35px;
    }
    #slider{
        width: 100%;
        accent-color: #0375AC;
    }
    .mc-input .container .inner .field, .mc-input .container .inner .field .input{
        background: red;
    }
    .mc-input .container .inner .field, .mc-input .container .inner .field{
        background: red;
    }
    .range-value{
        position: absolute;
        top: -40%;
        margin: 0 22px 0 22px;
    }
    .range-value span{
        width: 160px;
        height: 24px;
        line-height: 24px;
        text-align: center;
        background: #0375AC;
        color: #fff;
        font-size: 12px;
        display: block;
        position: absolute;
        left: 50%;
        transform: translate(-50%, 0);
        border-radius: 6px;
        border: 1px solid #545454;
    }
    .range-value span:before,
    .range-value span:after{
        content: "";
        position: absolute;
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        top: 100%;
        left: 50%;
        margin-left: -5px;
    }
    .range-value span:before{
        border-top: 5px solid #545454;
    }
    .range-value span:after{
        border-top: 6px solid #0375AC;
        margin-top: -2px;
        z-index: 1;
    }`
    ;

    @property({type: Number, reflect: true}) private playback: number = 1;
    @property({type: Number, reflect: true}) private interval: number = 1;
    @property({type: Number, reflect: true}) private duration: number = 7;
    @property({type: Number, reflect: true}) private progressValue: number = 0;
    @property({type: Boolean, reflect: true}) private disablePlayer: boolean = false;
    @property() private progress: any;

    private days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    private labels: string[] = [];
    private sliderElement?: HTMLInputElement;

    constructor() {
        super();
        this.getLabels = this.getLabels.bind(this);
        this.getMinRange = this.getMinRange.bind(this);
        this.getMaxRange = this.getMaxRange.bind(this);
        this.getStep = this.getStep.bind(this);
        this.togglePlay = this.togglePlay.bind(this);
        this.setBubble = this.setBubble.bind(this);
        this.onSliderPositionChange = this.onSliderPositionChange.bind(this);
        this.reset = this.reset.bind(this);
    }

    firstUpdated() {
        this.setBubble(new Date());
        this.sliderElement = <HTMLInputElement>this.shadowRoot?.querySelector("#slider")
        this.sliderElement?.addEventListener("input", () => {
            this.onSliderPositionChange();
        });
    }

    connectedCallback(): void {
        super.connectedCallback();
        VoyageShoreContextModel!.addEventListener(ShoreContextEvent.TimeLineViewToggle, this.reset);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        VoyageShoreContextModel!.removeEventListener(ShoreContextEvent.TimeLineViewToggle, this.reset);
    }

    getMinRange(){
        return 0;
    }

    getMaxRange(){
        return this.duration*24;
    }

    getStep(){
        return this.interval*this.playback;
    }

    getLabels(){
        this.labels = [];
        for(let i=0; i<this.duration; i++){
            let today = new Date();
            today.setDate(today.getDate() + i)
            let date = today.getDate();
            let day = today.getDay();
            let label = `${this.days[day%this.days.length]} ${date}`;
            this.labels.push(label);
        }
        let output = this.labels.map(label => {
            return html `<span class="label">${label}</span>`
        });
        return output;
    }

    getProgressValue(){
        return this.progressValue;
    }

    togglePlay(shouldPlay: boolean){
        if(shouldPlay){
            this.progress = setInterval(() => {
                let step = this.getStep();
                let max = this.getMaxRange();
                let now = new Date();
                if(this.progressValue + step > max){
                    this.progressValue = max;
                    this.dispatchEvent(new CustomEvent('timeline-complete'));
                    now.setHours(now.getHours() + this.progressValue);
                    this.setBubble(now);
                    this.updateSliderPosition();
                    return;
                }
                this.progressValue += step;
                now.setHours(now.getHours() + this.progressValue);
                this.setBubble(now);
                this.updateSliderPosition();
            }, 1000); 
        }else{
            clearInterval(this.progress);
        }
    }

    reset(){
        this.progressValue = 0;
        this.updatePlayerTime(this.progressValue);
    }

    step(direction: string){
        let step = this.getStep();
        let max = this.getMaxRange();
        if(direction === "forward"){
            if(this.progressValue + step > max){
                this.progressValue = max;
                this.updatePlayerTime(this.progressValue);
                return
            }
            this.progressValue += step;
            this.updatePlayerTime(this.progressValue);
        }
        if(direction === "backward"){
            this.progressValue -= step;
            this.updatePlayerTime(this.progressValue);
        }    
    }

    setBubble(date: Date) {
        let bubbleElement = <HTMLElement>this.shadowRoot?.querySelector('#rangeV');
        bubbleElement.innerHTML = `<span>${getFormattedDateTime(date)}</span>`;
        let max = this.getMaxRange();
        let percentage = (this.progressValue/max)*100;
        percentage = percentage > 0 ? percentage : 0; 
        bubbleElement.style.left = `calc(${percentage}% - (${4 * (percentage/10)}px))`;
    };

    onSliderPositionChange(){
        this.progressValue = Number(this.sliderElement?.value) || 0;
        this.updatePlayerTime(this.progressValue);
    }

    updatePlayerTime(progressValue: number){
        let now = new Date();
        now.setHours(now.getHours() + progressValue);
        this.dispatchEvent(new CustomEvent('slider-input', {
            detail: { focustime: now.getTime() },
        }));
        this.setBubble(now);
        this.updateSliderPosition();
    }

    updateSliderPosition(){
        if(this.sliderElement){
            this.sliderElement.value = this.progressValue.toString();
        }
    }

    render() {
        return html`
         <div class="input-container">
            <div class="range-value" id="rangeV"><span>00</span></div>
            <input type="range" id="slider" 
                min="${this.getMinRange()}" 
                max="${this.getMaxRange()}" 
                value="${this.progressValue}" 
                step="${this.getStep()}"
                ?disabled="${this.disablePlayer}"/>
            </div>
        <div class="labels">
            ${html`${this.getLabels()}`}
        </div>
    `;
    }
}